// Mock data for the Basket WNTD List App

export interface Product {
  id: string;
  name: string;
  price: number;
  currency: string;
  image: string;
  images?: string[];
  store: string;
  storeIcon?: string;
  originalPrice?: number;
  priceDropPercent?: number;
  inStock: boolean;
  addedAt: string;
}

export interface Collection {
  id: string;
  name: string;
  type: 'public' | 'collab' | 'gift' | 'private';
  icon: string;
  itemCount: number;
  totalValue: number;
  saved: number;
  collaborators: { name: string; avatar: string }[];
  hasPriceDrops: boolean;
  items: Product[];
}

export interface GiftCard {
  id: string;
  name: string;
  logo: string;
  cashbackPercent: number;
  category: string;
  isNew?: boolean;
  isPopular?: boolean;
  usableOnline: boolean;
  usableInStore: boolean;
}

export interface Notification {
  id: string;
  type: 'price_drop' | 'back_in_stock' | 'collection_update' | 'referral';
  title: string;
  message: string;
  image?: string;
  savings?: number;
  timestamp: string;
}

export interface AlternativePrice {
  id: string;
  store: string;
  storeUrl: string;
  price: number;
  originalPrice?: number;
  currency: string;
  inStock: boolean;
  deliveryInfo?: string;
  isBestPrice?: boolean;
}

// Mock alternative prices for products
export const mockAlternativePrices: Record<string, AlternativePrice[]> = {
  '1': [ // Nike Vomero
    { id: 'alt-1-1', store: 'Nike', storeUrl: 'nike.com', price: 154.99, currency: 'GBP', inStock: true, deliveryInfo: 'Free delivery' },
    { id: 'alt-1-2', store: 'JD Sports', storeUrl: 'jdsports.co.uk', price: 149.99, currency: 'GBP', inStock: true, deliveryInfo: '2-3 days', isBestPrice: true },
    { id: 'alt-1-3', store: 'Foot Locker', storeUrl: 'footlocker.co.uk', price: 154.99, currency: 'GBP', inStock: true, deliveryInfo: 'Next day available' },
    { id: 'alt-1-4', store: 'Size?', storeUrl: 'size.co.uk', price: 159.99, currency: 'GBP', inStock: false },
  ],
  '2': [ // T-Shirt
    { id: 'alt-2-1', store: '80sTees', storeUrl: '80stees.com', price: 54.99, originalPrice: 64.99, currency: 'USD', inStock: true, deliveryInfo: '5-7 days' },
    { id: 'alt-2-2', store: 'Amazon', storeUrl: 'amazon.com', price: 49.99, currency: 'USD', inStock: true, deliveryInfo: 'Prime 2-day', isBestPrice: true },
    { id: 'alt-2-3', store: 'eBay', storeUrl: 'ebay.com', price: 52.00, currency: 'USD', inStock: true, deliveryInfo: 'Varies' },
  ],
  '3': [ // OK USA T-Shirt
    { id: 'alt-3-1', store: '80sTees', storeUrl: '80stees.com', price: 39.99, originalPrice: 49.99, currency: 'USD', inStock: true, isBestPrice: true },
    { id: 'alt-3-2', store: 'Redbubble', storeUrl: 'redbubble.com', price: 42.99, currency: 'USD', inStock: true, deliveryInfo: '7-10 days' },
  ],
  '4': [ // Life Moves T-Shirt
    { id: 'alt-4-1', store: '80sTees', storeUrl: '80stees.com', price: 44.99, currency: 'USD', inStock: false },
    { id: 'alt-4-2', store: 'TeePublic', storeUrl: 'teepublic.com', price: 39.99, currency: 'USD', inStock: true, deliveryInfo: '5-7 days', isBestPrice: true },
  ],
  '5': [ // DeLonghi Kettle
    { id: 'alt-5-1', store: 'Amazon UK', storeUrl: 'amazon.co.uk', price: 55.00, currency: 'GBP', inStock: true, deliveryInfo: 'Prime next day' },
    { id: 'alt-5-2', store: 'Currys', storeUrl: 'currys.co.uk', price: 52.99, currency: 'GBP', inStock: true, deliveryInfo: 'Free delivery', isBestPrice: true },
    { id: 'alt-5-3', store: 'Argos', storeUrl: 'argos.co.uk', price: 54.99, currency: 'GBP', inStock: true, deliveryInfo: 'Same day collection' },
    { id: 'alt-5-4', store: 'John Lewis', storeUrl: 'johnlewis.com', price: 55.00, currency: 'GBP', inStock: true, deliveryInfo: '2-year guarantee' },
  ],
  '6': [ // Haden Kettle
    { id: 'alt-6-1', store: 'Amazon UK', storeUrl: 'amazon.co.uk', price: 60.00, originalPrice: 75.00, currency: 'GBP', inStock: true, deliveryInfo: 'Prime next day' },
    { id: 'alt-6-2', store: 'Wayfair', storeUrl: 'wayfair.co.uk', price: 57.99, currency: 'GBP', inStock: true, deliveryInfo: '3-5 days', isBestPrice: true },
    { id: 'alt-6-3', store: 'Robert Dyas', storeUrl: 'robertdyas.co.uk', price: 62.99, currency: 'GBP', inStock: false },
  ],
};

// Sample product images (using placeholder for now)
const productImages = [
  'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop', // Nike shoe
  'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=400&fit=crop', // T-shirt
  'https://images.unsplash.com/photo-1544816155-12df9643f363?w=400&h=400&fit=crop', // Kettle
  'https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=400&h=400&fit=crop', // Projector
  'https://images.unsplash.com/photo-1549298916-b41d501d3772?w=400&h=400&fit=crop', // Another shoe
  'https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=400&h=400&fit=crop', // Hoodie
  'https://images.unsplash.com/photo-1560343090-f0409e92791a?w=400&h=400&fit=crop', // Teddy bear
  'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400&fit=crop', // Watch
];

export const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Nike Vomero Plus Men\'s Road Running Shoes',
    price: 154.99,
    currency: 'GBP',
    image: productImages[0],
    images: [
      productImages[0],
      productImages[4],
      'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=400&h=400&fit=crop',
    ],
    store: 'nike.com',
    inStock: true,
    addedAt: '2024-01-10',
  },
  {
    id: '2',
    name: 'Negative Ghostrider T-Shirt - Top Gun',
    price: 54.99,
    currency: 'USD',
    image: productImages[1],
    images: [
      productImages[1],
      productImages[5],
    ],
    store: '80stees.com',
    originalPrice: 64.99,
    priceDropPercent: 15,
    inStock: true,
    addedAt: '2024-01-09',
  },
  {
    id: '3',
    name: 'OK USA Vintage Movie T-Shirt',
    price: 39.99,
    currency: 'USD',
    image: productImages[1],
    images: [productImages[1]],
    store: '80stees.com',
    originalPrice: 49.99,
    priceDropPercent: 20,
    inStock: true,
    addedAt: '2024-01-08',
  },
  {
    id: '4',
    name: 'Life Moves Pretty Fast T-Shirt',
    price: 44.99,
    currency: 'USD',
    image: productImages[1],
    images: [productImages[1]],
    store: '80stees.com',
    inStock: false,
    addedAt: '2024-01-07',
  },
  {
    id: '5',
    name: 'DeLonghi Icona Vintage Kettle',
    price: 55.00,
    currency: 'GBP',
    image: productImages[2],
    images: [
      productImages[2],
      'https://images.unsplash.com/photo-1594213114663-d94db9b6b5b0?w=400&h=400&fit=crop',
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=400&fit=crop',
    ],
    store: 'amazon.co.uk',
    inStock: true,
    addedAt: '2024-01-06',
  },
  {
    id: '6',
    name: 'Haden Perth Sleek Kettle - Slate Grey',
    price: 60.00,
    currency: 'GBP',
    image: productImages[2],
    images: [
      productImages[2],
      'https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=400&h=400&fit=crop',
    ],
    store: 'amazon.co.uk',
    originalPrice: 75.00,
    priceDropPercent: 20,
    inStock: true,
    addedAt: '2024-01-05',
  },
];

export const mockCollections: Collection[] = [
  {
    id: '1',
    name: 'Dream Kicks',
    type: 'public',
    icon: '👟',
    itemCount: 24,
    totalValue: 2450,
    saved: 125,
    collaborators: [{ name: 'Lex', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face' }],
    hasPriceDrops: false,
    items: mockProducts.filter(p => p.store === 'nike.com'),
  },
  {
    id: '2',
    name: 'Kettles',
    type: 'collab',
    icon: '🫖',
    itemCount: 11,
    totalValue: 270,
    saved: 2.50,
    collaborators: [
      { name: 'Lee Lee', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face' },
      { name: 'Sam', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face' },
    ],
    hasPriceDrops: true,
    items: mockProducts.filter(p => p.name.includes('Kettle')),
  },
  {
    id: '3',
    name: 'Foulds Primary School WNTD List',
    type: 'gift',
    icon: '🎁',
    itemCount: 83,
    totalValue: 1250,
    saved: 45,
    collaborators: [
      { name: 'Foulds School', avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face' },
    ],
    hasPriceDrops: true,
    items: [],
  },
  {
    id: '4',
    name: 'Gadgets',
    type: 'private',
    icon: '📱',
    itemCount: 8,
    totalValue: 890,
    saved: 0,
    collaborators: [],
    hasPriceDrops: false,
    items: [],
  },
];

export const mockGiftCards: GiftCard[] = [
  {
    id: '1',
    name: 'M&S',
    logo: 'M&S',
    cashbackPercent: 3.5,
    category: 'for-you',
    isPopular: true,
    usableOnline: true,
    usableInStore: true,
  },
  {
    id: '2',
    name: 'Tesco',
    logo: 'TESCO',
    cashbackPercent: 2.5,
    category: 'for-you',
    isPopular: true,
    usableOnline: false,
    usableInStore: true,
  },
  {
    id: '3',
    name: 'Mango',
    logo: 'MANGO',
    cashbackPercent: 1.5,
    category: 'for-you',
    isNew: true,
    usableOnline: true,
    usableInStore: true,
  },
  {
    id: '4',
    name: 'Lush UK',
    logo: 'LUSH',
    cashbackPercent: 5,
    category: 'for-you',
    isNew: true,
    usableOnline: true,
    usableInStore: true,
  },
  {
    id: '5',
    name: 'Costa Coffee',
    logo: 'COSTA',
    cashbackPercent: 4,
    category: 'food-drink',
    usableOnline: false,
    usableInStore: true,
  },
  {
    id: '6',
    name: 'Nandos',
    logo: 'NANDOS',
    cashbackPercent: 3,
    category: 'food-drink',
    usableOnline: true,
    usableInStore: true,
  },
  {
    id: '7',
    name: 'Booking.com',
    logo: 'BOOKING',
    cashbackPercent: 6,
    category: 'travel',
    isPopular: true,
    usableOnline: true,
    usableInStore: false,
  },
];

export const mockNotifications: Notification[] = [
  {
    id: '1',
    type: 'price_drop',
    title: 'Price Drop!',
    message: 'Swan SK14610WHT Nordic Kettle is now £37.50',
    image: productImages[2],
    savings: 2.50,
    timestamp: '9d',
  },
  {
    id: '2',
    type: 'collection_update',
    title: 'New item added',
    message: 'Damson Madder Anja Reversible Trench has just been added to Kettles',
    image: productImages[5],
    timestamp: '9d',
  },
  {
    id: '3',
    type: 'referral',
    title: 'Referral bonus unlocked!',
    message: 'Your friend joined. You earned £5!',
    timestamp: '13d',
  },
  {
    id: '4',
    type: 'price_drop',
    title: 'Big savings!',
    message: 'Shearer Chillback Accent Chair is now £1,270',
    image: productImages[7],
    savings: 125,
    timestamp: '15d',
  },
];

export const userProfile = {
  name: 'Alex',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
  walletBalance: 51.66,
  currency: 'GBP',
  country: 'United Kingdom',
  verified: true,
  priceDropCount: 13,
  backInStockCount: 4,
  totalSavings: 198.93,
};

// Feed post images
const feedImages = {
  sneakers1: 'https://images.unsplash.com/photo-1600269452121-4f2416e55c28?w=600&h=600&fit=crop',
  sneakers2: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=600&h=600&fit=crop',
  sneakers3: 'https://images.unsplash.com/photo-1560769629-975ec94e6a86?w=600&h=600&fit=crop',
  outfit1: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=600&h=600&fit=crop',
  outfit2: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=600&h=600&fit=crop',
  jacket1: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=600&h=600&fit=crop',
  jacket2: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=600&h=600&fit=crop',
  bag1: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&h=600&fit=crop',
  bag2: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=600&h=600&fit=crop',
  watch1: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&h=600&fit=crop',
};

import type { PollType } from '@/types/polls';

export interface FeedPost {
  id: string;
  pollType: PollType;
  author: {
    id?: string;
    username: string;
    avatar: string;
  };
  caption: string;
  images: { id: string; url: string }[];
  votes: { up: number; down: number } | { yes: number; no: number } | Record<string, number>;
  timestamp: string;
}

export const mockFeedPosts: FeedPost[] = [
  {
    id: 'feed-1',
    pollType: 'this-or-that',
    author: {
      id: 'user-sneaker-head',
      username: 'SNEAKER.HEAD',
      avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop&crop=face',
    },
    caption: 'White or black sneakers with this fit? 👟',
    images: [
      { id: 'img-1a', url: feedImages.sneakers1 },
      { id: 'img-1b', url: feedImages.sneakers2 },
    ],
    votes: { 'img-1a': 156, 'img-1b': 122 },
    timestamp: 'JUST NOW',
  },
  {
    id: 'feed-2',
    pollType: 'yes-no',
    author: {
      id: 'user-style-maven',
      username: 'STYLE.MAVEN',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face',
    },
    caption: 'Finally copped these 🔥 Should I keep them?',
    images: [
      { id: 'img-2', url: feedImages.sneakers3 },
    ],
    votes: { yes: 432, no: 12 },
    timestamp: '2H AGO',
  },
  {
    id: 'feed-3',
    pollType: 'yes-no',
    author: {
      id: 'user-minimal-fit',
      username: 'MINIMAL.FIT',
      avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face',
    },
    caption: 'Should I get this leather jacket for the office? 🤔',
    images: [
      { id: 'img-3a', url: feedImages.jacket1 },
    ],
    votes: { yes: 89, no: 23 },
    timestamp: '4H AGO',
  },
  {
    id: 'feed-4',
    pollType: 'pick-one',
    author: {
      id: 'user-luxe-vibes',
      username: 'LUXE.VIBES',
      avatar: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=100&h=100&fit=crop&crop=face',
    },
    caption: 'New season pieces just arrived ✨ Which is your fave?',
    images: [
      { id: 'img-4a', url: feedImages.outfit1 },
      { id: 'img-4b', url: feedImages.bag1 },
      { id: 'img-4c', url: feedImages.watch1 },
    ],
    votes: { 'img-4a': 234, 'img-4b': 189, 'img-4c': 144 },
    timestamp: '6H AGO',
  },
  {
    id: 'feed-5',
    pollType: 'this-or-that',
    author: {
      id: 'user-daily-drip',
      username: 'DAILY.DRIP',
      avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop&crop=face',
    },
    caption: 'Crossbody or tote for everyday? Help me decide! 👜',
    images: [
      { id: 'img-5a', url: feedImages.bag1 },
      { id: 'img-5b', url: feedImages.bag2 },
    ],
    votes: { 'img-5a': 98, 'img-5b': 105 },
    timestamp: '8H AGO',
  },
];

// Mock comments for feed posts
export interface MockComment {
  id: string;
  poll_id: string;
  user_id: string;
  content: string;
  parent_id: string | null;
  likes_count: number;
  created_at: string;
  updated_at: string;
  profile: {
    display_name: string;
    avatar_url: string;
  };
  isLikedByUser?: boolean;
  replies?: MockComment[];
}

export const mockComments: Record<string, MockComment[]> = {
  'feed-1': [
    {
      id: 'mock-comment-1a',
      poll_id: 'feed-1',
      user_id: 'mock-user-1',
      content: 'Definitely the white ones! They go with everything 🔥',
      parent_id: null,
      likes_count: 12,
      created_at: new Date(Date.now() - 30 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 30 * 60000).toISOString(),
      profile: {
        display_name: 'FashionFan',
        avatar_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
      },
      replies: [
        {
          id: 'mock-comment-1a-reply',
          poll_id: 'feed-1',
          user_id: 'mock-user-2',
          content: 'Agreed! White sneakers are so versatile',
          parent_id: 'mock-comment-1a',
          likes_count: 3,
          created_at: new Date(Date.now() - 15 * 60000).toISOString(),
          updated_at: new Date(Date.now() - 15 * 60000).toISOString(),
          profile: {
            display_name: 'SneakerLover',
            avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
          },
        },
      ],
    },
    {
      id: 'mock-comment-1b',
      poll_id: 'feed-1',
      user_id: 'mock-user-3',
      content: 'Black goes with more fits though 👀',
      parent_id: null,
      likes_count: 8,
      created_at: new Date(Date.now() - 45 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 45 * 60000).toISOString(),
      profile: {
        display_name: 'StreetStyle',
        avatar_url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
      },
    },
    {
      id: 'mock-comment-1c',
      poll_id: 'feed-1',
      user_id: 'mock-user-4',
      content: 'Both are clean tbh, get both! 😂',
      parent_id: null,
      likes_count: 5,
      created_at: new Date(Date.now() - 60 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 60 * 60000).toISOString(),
      profile: {
        display_name: 'MaxiMalist',
        avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
      },
    },
  ],
  'feed-2': [
    {
      id: 'mock-comment-2a',
      poll_id: 'feed-2',
      user_id: 'mock-user-1',
      content: 'Easy cop! These are fire 🔥🔥🔥',
      parent_id: null,
      likes_count: 24,
      created_at: new Date(Date.now() - 90 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 90 * 60000).toISOString(),
      profile: {
        display_name: 'FashionFan',
        avatar_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
      },
    },
    {
      id: 'mock-comment-2b',
      poll_id: 'feed-2',
      user_id: 'mock-user-5',
      content: 'Where did you get these? Need the link!',
      parent_id: null,
      likes_count: 6,
      created_at: new Date(Date.now() - 100 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 100 * 60000).toISOString(),
      profile: {
        display_name: 'KickSeeker',
        avatar_url: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face',
      },
      replies: [
        {
          id: 'mock-comment-2b-reply',
          poll_id: 'feed-2',
          user_id: 'user-style-maven',
          content: 'Got them from the Nike app! Limited drop 🙌',
          parent_id: 'mock-comment-2b',
          likes_count: 2,
          created_at: new Date(Date.now() - 80 * 60000).toISOString(),
          updated_at: new Date(Date.now() - 80 * 60000).toISOString(),
          profile: {
            display_name: 'STYLE.MAVEN',
            avatar_url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face',
          },
        },
      ],
    },
  ],
  'feed-3': [
    {
      id: 'mock-comment-3a',
      poll_id: 'feed-3',
      user_id: 'mock-user-2',
      content: 'Yes! Classic piece that never goes out of style 👔',
      parent_id: null,
      likes_count: 15,
      created_at: new Date(Date.now() - 3 * 60 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 3 * 60 * 60000).toISOString(),
      profile: {
        display_name: 'SneakerLover',
        avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      },
    },
    {
      id: 'mock-comment-3b',
      poll_id: 'feed-3',
      user_id: 'mock-user-6',
      content: 'Maybe try a blazer instead? More office-appropriate',
      parent_id: null,
      likes_count: 4,
      created_at: new Date(Date.now() - 3.5 * 60 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 3.5 * 60 * 60000).toISOString(),
      profile: {
        display_name: 'OfficeChic',
        avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      },
    },
    {
      id: 'mock-comment-3c',
      poll_id: 'feed-3',
      user_id: 'mock-user-3',
      content: 'Depends on your dress code but I say go for it! 🔥',
      parent_id: null,
      likes_count: 7,
      created_at: new Date(Date.now() - 4 * 60 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 4 * 60 * 60000).toISOString(),
      profile: {
        display_name: 'StreetStyle',
        avatar_url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
      },
    },
  ],
  'feed-4': [
    {
      id: 'mock-comment-4a',
      poll_id: 'feed-4',
      user_id: 'mock-user-1',
      content: 'That watch is beautiful ⌚✨',
      parent_id: null,
      likes_count: 18,
      created_at: new Date(Date.now() - 5 * 60 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 5 * 60 * 60000).toISOString(),
      profile: {
        display_name: 'FashionFan',
        avatar_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
      },
    },
    {
      id: 'mock-comment-4b',
      poll_id: 'feed-4',
      user_id: 'mock-user-4',
      content: 'The bag is so versatile, would pick that!',
      parent_id: null,
      likes_count: 11,
      created_at: new Date(Date.now() - 5.5 * 60 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 5.5 * 60 * 60000).toISOString(),
      profile: {
        display_name: 'MaxiMalist',
        avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
      },
    },
    {
      id: 'mock-comment-4c',
      poll_id: 'feed-4',
      user_id: 'mock-user-5',
      content: 'All of them honestly 😍 Hard to choose!',
      parent_id: null,
      likes_count: 9,
      created_at: new Date(Date.now() - 6 * 60 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 6 * 60 * 60000).toISOString(),
      profile: {
        display_name: 'KickSeeker',
        avatar_url: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face',
      },
    },
  ],
  'feed-5': [
    {
      id: 'mock-comment-5a',
      poll_id: 'feed-5',
      user_id: 'mock-user-2',
      content: 'Tote all the way for everyday use! More space 👜',
      parent_id: null,
      likes_count: 14,
      created_at: new Date(Date.now() - 7 * 60 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 7 * 60 * 60000).toISOString(),
      profile: {
        display_name: 'SneakerLover',
        avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      },
    },
    {
      id: 'mock-comment-5b',
      poll_id: 'feed-5',
      user_id: 'mock-user-6',
      content: 'Crossbody is more practical, hands-free is the way!',
      parent_id: null,
      likes_count: 10,
      created_at: new Date(Date.now() - 7.5 * 60 * 60000).toISOString(),
      updated_at: new Date(Date.now() - 7.5 * 60 * 60000).toISOString(),
      profile: {
        display_name: 'OfficeChic',
        avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      },
      replies: [
        {
          id: 'mock-comment-5b-reply',
          poll_id: 'feed-5',
          user_id: 'user-daily-drip',
          content: 'Good point! I do need my hands free a lot 🤔',
          parent_id: 'mock-comment-5b',
          likes_count: 1,
          created_at: new Date(Date.now() - 7 * 60 * 60000).toISOString(),
          updated_at: new Date(Date.now() - 7 * 60 * 60000).toISOString(),
          profile: {
            display_name: 'DAILY.DRIP',
            avatar_url: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop&crop=face',
          },
        },
      ],
    },
  ],
};